import { useStrapiLang } from '@autobid/strapi-integration/composable/useStrapiLang'
import { SLUGS_BLACKLIST } from '@autobid/strapi-integration/constants/SLUGS_BLACKLIST'
import { useQueryClient } from '@tanstack/vue-query'

export interface PageProps {
  slug: string
  draft?: boolean
  draftCollection?: string
  // allow to display the slot even if the pageData is empty
  allowEmpty?: boolean
  ignoreBlacklist?: boolean
}

interface PageParams extends PageProps {
  locale: string
}

export function getPathName(slug: string, locale: string) {
  return slug.startsWith('/') ? slug : `/${locale}/${slug}`
}

export function shouldFetchPage(props: PageParams): boolean {
  const lastPartOfSlug = props.slug.split('/').reverse()[0]

  if (!props.ignoreBlacklist && SLUGS_BLACKLIST.includes(lastPartOfSlug)) {
    return false
  }
  return true
}

export function getPageQueryKey(params: PageParams) {
  return ['page', params, getPathName(params.slug, params.locale)]
}

export function usePage() {
  const { strapiLang: locale } = useStrapiLang()
  const route = useRoute()
  const urlParams = route.fullPath?.split('?')?.[1] ?? ''
  const queryClient = useQueryClient()
  const { $trpc } = useNuxtApp()

  async function fetchPage(props: PageProps) {
    if (!shouldFetchPage({ ...props, locale: locale.value })) {
      return null
    }

    return await $trpc.cms.getPage.query({
      path: getPathName(props.slug, locale.value),
      locale: locale.value,
      ...(props.draft ? { draft: true } : {}),
      ...(props.draftCollection ? { draftCollection: props.draftCollection } : {}),
      ...(urlParams.length && { params: urlParams }),
    })
  }

  const prefetchPage = async (props: PageProps) => {
    const paramsWithLocale: PageParams = { ...props, locale: locale.value }
    const queryKey = getPageQueryKey(paramsWithLocale)

    const isAlreadyPrefetched = queryClient.getQueryData(queryKey) !== undefined

    if (isAlreadyPrefetched)
      return

    const data = await fetchPage(paramsWithLocale)
    queryClient.setQueryData(getPageQueryKey(paramsWithLocale), data)
  }

  return {
    getPageQueryKey,
    fetchPage,
    prefetchPage,
  }
}
